import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, AppBar, Toolbar, Typography, BottomNavigation, BottomNavigationAction,
  Paper, IconButton, Menu, MenuItem, CircularProgress, Badge, Snackbar, Button,
} from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ChatIcon from '@mui/icons-material/Chat';
import GroupsIcon from '@mui/icons-material/Groups';
import DescriptionIcon from '@mui/icons-material/Description';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { useTechnicianAuth } from '../../contexts/TechnicianAuthContext';
import TechnicianLogin from './TechnicianLogin';
import ChangePin from './ChangePin';
import MyTasks from './MyTasks';
import TaskDetail from './TaskDetail';
import AssignTasks from './AssignTasks';
import CreateTask from './CreateTask';
import HtmlDocs, { HtmlDocScreen } from './HtmlDocs';
import Chats, { ChatScreen } from './Chats';
import { chatAPI } from '../../services/api';
import { registerServiceWorker, syncPushSubscription, setAppBadge } from '../../services/push';
import PushTestDialog from '../../components/common/PushTestDialog';

const UNREAD_POLL_MS = 20000;

// El chat a abrir llega en la URL cuando la app se abre desde una notificacion
const chatFromUrl = () => {
  const id = Number(new URLSearchParams(window.location.search).get('chat'));
  return Number.isFinite(id) && id > 0 ? id : null;
};

/**
 * Shell de la app movil (tecnicos y gestores con PIN). No comparte layout con
 * el gestor: sin barra lateral, navegacion inferior y todo pensado para el movil.
 * Es la que se instala como app (manifest + service worker) y la que envuelve
 * el APK.
 */
export default function TechnicianApp() {
  const { technician, loading, logout } = useTechnicianAuth();
  const [tab, setTab] = useState('chats');
  const [openChatId, setOpenChatId] = useState(chatFromUrl);
  const [openTaskId, setOpenTaskId] = useState(null);
  const [openDoc, setOpenDoc] = useState(null);
  const [creating, setCreating] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [unread, setUnread] = useState(0);
  const [toast, setToast] = useState(null);     // aviso de un mensaje de otro chat
  const [installPrompt, setInstallPrompt] = useState(null);
  const [pushTestOpen, setPushTestOpen] = useState(false);

  // El service worker se registra aunque no haya sesion: sin el, el navegador
  // no ofrece instalar la app.
  useEffect(() => { registerServiceWorker(); }, []);

  // Chrome ofrece instalar con este evento; se guarda para el menu de la cuenta
  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener('beforeinstallprompt', onPrompt);
    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, []);

  const loggedIn = !!technician && !technician.must_change_pin;

  // Con el permiso ya dado, se refresca la suscripcion en cada arranque: el
  // navegador puede cambiar el endpoint, y el movil puede ser de otra persona.
  useEffect(() => {
    if (loggedIn) syncPushSubscription().catch(() => {});
  }, [loggedIn]);

  const updateUnread = useCallback((total) => {
    setUnread(total);
    setAppBadge(total);
  }, []);

  const refreshUnread = useCallback(() => {
    chatAPI.unread().then((res) => updateUnread(res.data.total)).catch(() => {});
  }, [updateUnread]);

  useEffect(() => {
    if (!loggedIn) return undefined;
    refreshUnread();
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') refreshUnread();
    }, UNREAD_POLL_MS);
    return () => clearInterval(timer);
  }, [loggedIn, refreshUnread]);

  // Mensajes del service worker: un push con la app en pantalla, o la
  // pulsacion de una notificacion con la app ya abierta
  useEffect(() => {
    const onMessage = (event) => {
      const { type, data, projectId } = event.data || {};
      if (type === 'open-chat' && projectId) {
        setOpenTaskId(null);
        setOpenDoc(null);
        setCreating(false);
        setOpenChatId(Number(projectId));
      }
      if (type === 'chat-push') {
        refreshUnread();
        setToast((current) => (Number(data?.project_id) === Number(openChatId) ? current : data));
      }
    };
    navigator.serviceWorker?.addEventListener('message', onMessage);
    return () => navigator.serviceWorker?.removeEventListener('message', onMessage);
  }, [openChatId, refreshUnread]);

  const closeChat = useCallback(() => {
    setOpenChatId(null);
    refreshUnread();
    // Si se entro por ?chat=, se limpia para que recargar no lo vuelva a abrir
    if (window.location.search.includes('chat=')) {
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, [refreshUnread]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center',
                 minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!technician) return <TechnicianLogin />;
  if (technician.must_change_pin) return <ChangePin />;

  const isTeamLeader = !!technician.is_team_leader;

  // Pantallas a pantalla completa, por encima de la navegacion
  if (openChatId) {
    return <ChatScreen projectId={openChatId} onBack={closeChat} />;
  }
  if (creating) {
    return (
      <CreateTask
        onCancel={() => setCreating(false)}
        onDone={(taskId) => { setCreating(false); setOpenTaskId(taskId); }}
      />
    );
  }
  if (openTaskId) {
    return (
      <TaskDetail
        taskId={openTaskId}
        onBack={() => { setOpenTaskId(null); setReloadKey((k) => k + 1); }}
      />
    );
  }
  if (openDoc) {
    return <HtmlDocScreen doc={openDoc} onBack={() => setOpenDoc(null)} />;
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#fafafa' }}>
      <AppBar position="sticky" elevation={1}>
        <Toolbar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {{ chats: 'Chats', assign: 'Tareas del equipo', html: 'Documentos' }[tab] || 'Mis tareas'}
            </Typography>
            <Typography variant="caption">{technician.name}</Typography>
          </Box>
          <IconButton color="inherit" onClick={(e) => setMenuAnchor(e.currentTarget)}
            aria-label="Cuenta">
            <AccountCircleIcon />
          </IconButton>
          <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
            <MenuItem disabled>
              {technician.login_code} {isTeamLeader ? '· Jefe de equipo' : ''}
            </MenuItem>
            {installPrompt && (
              <MenuItem onClick={() => {
                setMenuAnchor(null);
                installPrompt.prompt();
                setInstallPrompt(null);
              }}>
                Instalar la app
              </MenuItem>
            )}
            <MenuItem onClick={() => { setMenuAnchor(null); setPushTestOpen(true); }}>
              Probar notificaciones
            </MenuItem>
            <MenuItem onClick={() => { setMenuAnchor(null); logout(); }}>Salir</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {tab === 'chats' && (
        <Box sx={{ pb: 8 }}>
          <Chats onOpenChat={setOpenChatId} onUnreadChange={updateUnread} />
        </Box>
      )}
      {tab === 'tasks' && (
        <MyTasks key={reloadKey} onOpenTask={setOpenTaskId} />
      )}
      {tab === 'assign' && isTeamLeader && (
        <AssignTasks key={reloadKey} onOpenTask={setOpenTaskId}
          onCreateTask={() => setCreating(true)} />
      )}
      {tab === 'html' && <HtmlDocs onOpenDoc={setOpenDoc} />}

      <Paper sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 10 }} elevation={3}>
        <BottomNavigation showLabels value={tab} onChange={(_, v) => setTab(v)}>
          <BottomNavigationAction label="Chats" value="chats"
            icon={<Badge badgeContent={unread} color="success" max={99}><ChatIcon /></Badge>} />
          <BottomNavigationAction label="Mis tareas" value="tasks" icon={<AssignmentIcon />} />
          {isTeamLeader && (
            <BottomNavigationAction label="Equipo" value="assign" icon={<GroupsIcon />} />
          )}
          <BottomNavigationAction label="HTML" value="html" icon={<DescriptionIcon />} />
        </BottomNavigation>
      </Paper>

      <PushTestDialog open={pushTestOpen} onClose={() => setPushTestOpen(false)} />

      <Snackbar open={!!toast} autoHideDuration={5000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        message={toast ? `${toast.title}: ${toast.body}` : ''}
        action={toast?.project_id ? (
          <Button color="secondary" size="small"
            onClick={() => { setOpenChatId(Number(toast.project_id)); setToast(null); }}>
            Ver
          </Button>
        ) : null} />
    </Box>
  );
}
